import "../styles/login.scss";
import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  Auth,
  sendPasswordResetEmail,
} from "firebase/auth";

import logo from "../assets/img/partner_img/FFF_Logo_Orange_Stacked.png";
import { useContext } from "react";
import { Context } from "../context/AuthContext";
import { useEffect } from "react";

import GoogleIcon from "../assets/img/google.png";
import AppleIcon from "../assets/img/apple.png";

// Partner logos
import ffipLogo from "../assets/img/partner_img/ffip_logo.png";
import flysmithLogo from "../assets/img/partner_img/flysmith_logo.png";
import lidrigLogo from "../assets/img/partner_img/lidrig_logo.png";
import mainleyLogo from "../assets/img/partner_img/mainley_logo.png";
import riverratLogo from "../assets/img/partner_img/riverrat_logo.png";

import appScreenshot from "../assets/img/partner_img/IMG_5339.png";

import { Visibility, VisibilityOff, MailOutline, LockOutlined } from "@mui/icons-material";

/**
 * Login / Sign Up Page for the application
 */
const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useContext(Context);
  const auth = getAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Determine initial mode from URL param or default to signup
  const modeParam = searchParams.get("mode");
  const [loginSignUp, setLoginSignUp] = useState(modeParam === "login" ? "login" : "signup");
  const [forgotPasswordState, setForgotPassword] = useState(false);
  const [forgotResetSent, setForgotResetSent] = useState(false);
  const [showInvalidPassword, setShowInvalidPassword] = useState(false);
  const [authError, setAuthError] = useState("");

  const from = location.state?.from?.pathname || "/select-plan";
  const searchParamsState = location.state?.from?.search || "";

  useEffect(() => {
    if (!!user?.user) {
      if (!!from) {
        navigate(`${from}${searchParamsState && searchParamsState}`, { replace: true });
        return;
      }
      navigate("/select-plan");
    }
  });

  useEffect(() => {
    getRedirectResult(auth).catch((error) => {
      if (error.code && error.code !== "auth/no-auth-event") {
        console.error("Redirect auth error:", error.code, error.message);
        setAuthError("Sign-in failed. Please try again.");
      }
    });
  }, [auth]);

  const handleEmailLogin = async (e: any) => {
    e.preventDefault();
    setAuthError("");
    signInWithEmailAndPassword(auth, email, password)
      .then((user) => {
        // Successful login
      })
      .catch((e) => {
        const errorCode = e.code;
        if (errorCode === "auth/wrong-password" || errorCode === "auth/invalid-credential") {
          setShowInvalidPassword(true);
          setAuthError("Invalid email or password");
        } else if (errorCode === "auth/user-not-found") {
          setAuthError("No account found with this email");
        } else {
          setAuthError("An error occurred. Please try again.");
        }
        console.error("Auth Error", e);
      });
  };

  const handleEmailSignup = async (e: any) => {
    e.preventDefault();
    setShowInvalidPassword(false);
    setAuthError("");
    createUserWithEmailAndPassword(auth, email, password)
      .then((user) => {
        // Successful signup
      })
      .catch((e) => {
        if (e.code === "auth/email-already-in-use") {
          setAuthError("An account with this email already exists");
        } else if (e.code === "auth/weak-password") {
          setAuthError("Password must be at least 6 characters");
        } else {
          setAuthError("An error occurred. Please try again.");
        }
        console.error("Auth Error", e);
      });
  };

  const handleAppleLogin = (e: any) => {
    e.preventDefault();
    setShowInvalidPassword(false);
    setAuthError("");
    const provider = new OAuthProvider("apple.com");
    signInWithPopupHandler(auth, provider);
  };

  const handleGoogleLogin = (e: any) => {
    e.preventDefault();
    setShowInvalidPassword(false);
    setAuthError("");
    const provider = new GoogleAuthProvider();
    signInWithPopupHandler(auth, provider);
  };

  const forgotPassword = () => {
    setShowInvalidPassword(false);
    setAuthError("");
    setForgotPassword(true);
  };

  const cancelForgotPassword = () => {
    setForgotPassword(false);
  };

  const sendPasswordReset = () => {
    sendPasswordResetEmail(auth, email)
      .then(() => {
        setForgotResetSent(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const validateResetForm = () => {
    return !!email;
  };

  const validateLoginSignUpForm = () => {
    if (!!email && !!password) {
      return true;
    }
  };

  const signInWithPopupHandler = (auth: Auth, provider: any) => {
    signInWithRedirect(auth, provider).catch((error) => {
      console.error(error.code, error.message);
      setAuthError("Sign-in failed. Please try again.");
    });
  };

  const renderForgotPassword = () => {
    if (forgotResetSent) {
      return (
        <div className="login-form-container">
          <div className="login-form-header">
            <img className="login-logo" src={logo} alt="FlyFishFinder" />
          </div>
          <h2 className="login-heading">Reset Password</h2>
          <p className="login-subtext">
            We have sent you an email with instructions to reset your password.
          </p>
          <div className="button-group full-width">
            <button
              className="primary fill hover-shadow login-submit-btn"
              onClick={() => {
                setForgotResetSent(false);
                setForgotPassword(false);
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="login-form-container">
        <div className="login-form-header">
          <img className="login-logo" src={logo} alt="FlyFishFinder" />
        </div>
        <h2 className="login-heading">Reset Password</h2>
        <p className="login-subtext">
          Enter the email for your account, we will send you reset instructions.
        </p>
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); sendPasswordReset(); }}>
          <div className="login-field">
            <label>Email</label>
            <div className="login-input-wrap">
              <MailOutline className="login-input-icon" />
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                autoComplete="email"
                onChange={(e) => setEmail(e?.target?.value)}
              />
            </div>
          </div>
          <button
            disabled={!validateResetForm()}
            className="primary fill hover-shadow login-submit-btn"
            type="submit"
          >
            Send
          </button>
          <button
            className="primary outline hover-shadow login-submit-btn"
            onClick={(e) => {
              e.preventDefault();
              cancelForgotPassword();
            }}
          >
            Cancel
          </button>
        </form>
      </div>
    );
  };

  const renderSignupForm = () => (
    <div className="login-form-container">
      <div className="login-form-header">
        <img className="login-logo" src={logo} alt="FlyFishFinder" />
      </div>
      <h2 className="login-heading">Make an account to get started</h2>

      <form className="login-form" onSubmit={(e) => handleEmailSignup(e)}>
        <div className="login-field">
          <label>Email</label>
          <div className="login-input-wrap">
            <MailOutline className="login-input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              autoComplete="email"
              onChange={(e) => setEmail(e?.target?.value)}
            />
          </div>
        </div>

        <div className="login-field">
          <label>Password</label>
          <div className="login-input-wrap">
            <LockOutlined className="login-input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              onChange={(e) => setPassword(e?.target?.value)}
            />
            <button
              type="button"
              className="login-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </button>
          </div>
        </div>

        <div className="login-remember">
          <label className="login-checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>Remember Me</span>
          </label>
        </div>

        {!!authError && <p className="login-error">{authError}</p>}

        <button
          disabled={!validateLoginSignUpForm()}
          className="primary fill hover-shadow login-submit-btn"
          type="submit"
        >
          Sign Up
        </button>
      </form>

      <div className="divider">
        <span className="divider-text">Or</span>
      </div>

      <div className="login-social-buttons">
        <button className="generic full-round hover-shadow login-social-btn" onClick={(e) => handleGoogleLogin(e)}>
          <img src={GoogleIcon} alt="Google" />
          Sign up with Google
        </button>
        <button className="generic full-round hover-shadow login-social-btn" onClick={(e) => handleAppleLogin(e)}>
          <img src={AppleIcon} alt="Apple" />
          Sign up with Apple
        </button>
      </div>

      <p className="login-switch-text">
        Have an account?{" "}
        <span
          className="link"
          tabIndex={0}
          onClick={() => setLoginSignUp("login")}
          onKeyUp={(e) => e.key === "Enter" && setLoginSignUp("login")}
        >
          Sign in.
        </span>
      </p>
    </div>
  );

  const renderLoginForm = () => (
    <div className="login-form-container">
      <div className="login-form-header">
        <img className="login-logo" src={logo} alt="FlyFishFinder" />
      </div>
      <h2 className="login-heading">Welcome Back!</h2>

      <form className="login-form" onSubmit={(e) => handleEmailLogin(e)}>
        <div className="login-field">
          <label>Email</label>
          <div className="login-input-wrap">
            <MailOutline className="login-input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              autoComplete="email"
              onChange={(e) => setEmail(e?.target?.value)}
            />
          </div>
        </div>

        <div className="login-field">
          <label>Password</label>
          <div className="login-input-wrap">
            <LockOutlined className="login-input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              onChange={(e) => setPassword(e?.target?.value)}
            />
            <button
              type="button"
              className="login-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </button>
          </div>
        </div>

        <div className="login-remember">
          <label className="login-checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>Remember Me</span>
          </label>
          <span
            className="link login-forgot-link"
            tabIndex={0}
            onClick={() => forgotPassword()}
            onKeyUp={(e) => e.key === "Enter" && forgotPassword()}
          >
            Forgot Password?
          </span>
        </div>

        {!!authError && <p className="login-error">{authError}</p>}
        {showInvalidPassword && !authError && <p className="login-error">Invalid Password</p>}

        <button
          disabled={!validateLoginSignUpForm()}
          className="primary fill hover-shadow login-submit-btn"
          type="submit"
        >
          Login
        </button>
      </form>

      <div className="divider">
        <span className="divider-text">Or</span>
      </div>

      <div className="login-social-buttons">
        <button className="generic full-round hover-shadow login-social-btn" onClick={(e) => handleGoogleLogin(e)}>
          <img src={GoogleIcon} alt="Google" />
          Sign up with Google
        </button>
        <button className="generic full-round hover-shadow login-social-btn" onClick={(e) => handleAppleLogin(e)}>
          <img src={AppleIcon} alt="Apple" />
          Sign up with Apple
        </button>
      </div>

      <p className="login-switch-text">
        Don't have an account?{" "}
        <span
          className="link"
          tabIndex={0}
          onClick={() => setLoginSignUp("signup")}
          onKeyUp={(e) => e.key === "Enter" && setLoginSignUp("signup")}
        >
          Sign up.
        </span>
      </p>
    </div>
  );

  return (
    <div className="login-page-new">
      <div className="login-page-left">
        {forgotPasswordState
          ? renderForgotPassword()
          : loginSignUp === "signup"
            ? renderSignupForm()
            : renderLoginForm()
        }

        {/* Partner logos at bottom */}
        <div className="login-partners">
          <img src={mainleyLogo} alt="Mainely Outdoors" />
          <img src={riverratLogo} alt="River Rat" />
          <img src={flysmithLogo} alt="The Fly Smith" />
          <img src={ffipLogo} alt="FFIP" />
          <img src={lidrigLogo} alt="Lid Rig" />
        </div>
      </div>

      <div className="login-page-right">
        <div className="login-phone-mockup">
          <div className="iphone-frame">
            <div className="iphone-silent"></div>
            <div className="iphone-volume-up"></div>
            <div className="iphone-volume-down"></div>
            <div className="iphone-power"></div>
            <div className="iphone-screen">
              <div className="iphone-notch"></div>
              <img src={appScreenshot} alt="FlyFishFinder App Preview" />
            </div>
            <div className="iphone-home-bar"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
